import React, { useEffect, useMemo, useState } from 'react';
import { isRttsEnabled, isRttsUseMock } from '../../../config/featureFlags';
import { useLeague } from '../../../lsc/hooks/useLeague';
import type { LscArchiveItem } from '../../../lsc/types';
import { getRttsGateFields } from '../api';
import { gateFieldsFromArchive } from '../lib/normalizeGateFields';
import type { RttsGateFields, RttsUserRole } from '../types';
import RunTheTapeButton from './RunTheTapeButton';

export type ArchiveRunTheTapeSlotProps = {
  leagueId: string;
  archive: LscArchiveItem;
  /** Optional display hints when mock remaps fixture fields onto a real archive id. */
  matchupHint?: string;
  playedAtHint?: string;
};

/**
 * Per-game mount (entry point #1) for the shared RunTheTapeButton.
 * Live: reads gate fields off the archive object already loaded by LSC (PR #143).
 * Mock: keeps the fixture-driven getRttsGateFields path for local/CI.
 */
const ArchiveRunTheTapeSlot: React.FC<ArchiveRunTheTapeSlotProps> = ({
  leagueId,
  archive,
  matchupHint,
  playedAtHint,
}) => {
  const enabled = isRttsEnabled();
  const useMock = isRttsUseMock();
  const { isCommissioner } = useLeague();
  const userRole: RttsUserRole = isCommissioner ? 'commissioner' : 'member';
  const [mockGates, setMockGates] = useState<RttsGateFields | null>(null);

  const liveGates = useMemo(() => {
    if (!enabled || useMock) return null;
    return gateFieldsFromArchive(archive, {
      matchup: matchupHint,
      played_at: playedAtHint,
    });
  }, [enabled, useMock, archive, matchupHint, playedAtHint]);

  useEffect(() => {
    if (!enabled || !useMock || !leagueId || !archive.id) {
      setMockGates(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getRttsGateFields(leagueId, archive.id);
        if (cancelled) return;
        setMockGates({
          ...data,
          matchup: matchupHint || data.matchup,
          played_at: playedAtHint || data.played_at,
        });
      } catch {
        if (!cancelled) setMockGates(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, useMock, leagueId, archive.id, matchupHint, playedAtHint]);

  if (!enabled) return null;
  const gates = useMock ? mockGates : liveGates;
  if (!gates) return null;

  return (
    <div data-testid="archive-rtts-slot">
      <RunTheTapeButton leagueId={leagueId} gates={gates} userRole={userRole} />
    </div>
  );
};

export default ArchiveRunTheTapeSlot;
